/**
 * The employee absence overview (Oct 2026: moved here from web/src/absence/AbsenceOverview.tsx unchanged) — the
 * vacation and leave balances exactly as the Absence dashboard and the Home balances card show them. Shared so
 * the server can fill announcement placeholders ("{{ptoRemaining}}") with the same numbers the person sees.
 */
import { resolveLeaveTypes, vacationEntitlementDays, accruingPolicy, vacationAnnualEntitlementDays, computeCategorySummary, isOvertimeCompensationType, effectiveDependentPolicy, vacationWaterfall, dependentEntitlementDays, hrPolicyStatuses, findAbsencePolicy, effectiveAbsencePolicyId, leaveYearForUser, leaveYearBounds, holidaysFor, } from './absence.js';
/** Everything the tiles need, computed from the user doc + config
 *  + requests (exactly the employee dashboard's logic). */
export function computeAbsenceOverview(user, config, requests, opts = {}) {
    const leaveTypes = resolveLeaveTypes(config);
    const year = new Date().getFullYear();
    const todayISO = new Date().toISOString().slice(0, 10);
    // Vacation follows the policy's LEAVE YEAR (Oct–Sep for Chile/Mexico on Remote); other types stay calendar.
    const leaveYear = user ? leaveYearForUser(user, config, todayISO) : leaveYearBounds(1, todayISO);
    // Totals per category: PTO = the effective policy's entitlement (pro-rated +
    // carry-over); WfA + Sick are global config.
    const vacationTotal = user ? vacationEntitlementDays(user, config, leaveYear, todayISO) : null;
    const totalFor = {
        Vacation: vacationTotal,
        'Work from anywhere': config?.absenceWorkFromAnywhereDays ?? null,
        // ONE sick type since Sep 2026 ('Sick leave'; legacy 'Sick' kept as alias).
        'Sick leave': config?.absenceSickDaysAllowance ?? null,
        Sick: config?.absenceSickDaysAllowance ?? null,
    };
    const reqs = requests ?? [];
    // Dependent vacation policy (Sep 2026): its days open only once the primary is
    // fully allocated; attribution is a waterfall computed here from the requests.
    const dependentPolicy = user ? effectiveDependentPolicy(user, config, todayISO) : null;
    // The user's OWN primary (a dependent may follow several primaries; a top-up add-on follows any).
    const primaryPolicy = user ? findAbsencePolicy(config, effectiveAbsencePolicyId(user, todayISO)) : null;
    const primaryPolicyName = user ? (primaryPolicy?.name ?? opts.primaryFallback ?? 'your main policy') : '';
    // The person's holiday calendar makes the split of a year-crossing request exact (27 Oct 2026).
    const holidays = user ? holidaysFor(config, user.holidayCalendarId) : null;
    // Accruing policies (daily / monthly, e.g. Remote): the Total is what has accrued so far; the tile also shows the
    // whole leave year (annual entitlement, remaining for the year) so people can plan beyond today's accrual.
    const accruing = user ? accruingPolicy(user, config, todayISO) : null;
    const vacationNote = accruing ? `accrues ${accruing.accrualMethod} · ${accruing.annualEntitlementDays} days per year` : null;
    const vacationAnnual = accruing && user ? vacationAnnualEntitlementDays(user, config, leaveYear) : null;
    // The add-on opens once the YEAR's days are used (Michael, 27 Oct 2026: "remaining for the year" = 0), so on an
    // accruing policy the waterfall runs on the annual entitlement, not on what has accrued so far.
    const waterfallPrimaryTotal = accruing ? vacationAnnual : vacationTotal;
    const waterfall = dependentPolicy && waterfallPrimaryTotal != null
        ? vacationWaterfall(reqs, 'Vacation', waterfallPrimaryTotal, dependentEntitlementDays(dependentPolicy, primaryPolicy), leaveYear, todayISO, holidays)
        : null;
    const hrStatuses = user ? hrPolicyStatuses(user, config, reqs, todayISO) : [];
    // Remote.com (employer of record) balances, as Remote computes them (nightly sync).
    const remoteLeave = user?.remoteLeave ?? null;
    const vacationAnnualSummary = accruing && vacationAnnual != null
        ? (waterfall ? waterfall.primary : computeCategorySummary(reqs, 'Vacation', vacationAnnual, leaveYear, todayISO, holidays))
        : null;
    const summaryFor = (name) => {
        if (name !== 'Vacation')
            return computeCategorySummary(reqs, name, totalFor[name] ?? null, year, todayISO, holidays);
        if (!waterfall)
            return computeCategorySummary(reqs, 'Vacation', vacationTotal, leaveYear, todayISO, holidays);
        if (!accruing)
            return waterfall.primary;
        // Accruing + add-on: the accrued tile shows the days attributed to the main policy against today's accrual.
        const p = waterfall.primary;
        return { total: vacationTotal, taken: p.taken, plannedApproved: p.plannedApproved, plannedRequested: p.plannedRequested, remaining: vacationTotal == null ? null : vacationTotal - p.taken - p.plannedApproved - p.plannedRequested };
    };
    // One card per configured leave type (feedback: show ALL absence options, not
    // just the classic three). Overtime compensation is hour-based and lives on
    // the Time Tracking side, so it gets no day card.
    const boxes = leaveTypes
        .filter((t) => !isOvertimeCompensationType(leaveTypes, t.name))
        .map((t) => ({ label: t.name, type: t.name, s: summaryFor(t.name), note: t.name === 'Vacation' ? vacationNote : null }));
    return { leaveTypes, year, leaveYear, todayISO, vacationTotal, dependentPolicy, primaryPolicyName, waterfall, hrStatuses, summaryFor, boxes, remoteLeave, accruing, vacationAnnual, vacationAnnualSummary };
}
//# sourceMappingURL=absenceOverview.js.map